// src/components/ErrorBoundary.tsx — Catch-all para errores React no manejados
// Spec: UX — pantalla de error con botón "Reintentar" y reporte básico
import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

interface Props   { children: ReactNode; fallback?: ReactNode }
interface State   { hasError: boolean; error?: Error }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // En producción enviar a Sentry/DD; por ahora sólo console
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  retry = () => {
    if (
      this.state.error?.message?.includes('dynamically imported module') ||
      this.state.error?.message?.includes('Failed to fetch')
    ) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div
          className="min-h-screen flex items-center justify-center p-8"
          style={{ background: 'var(--color-bg-primary)' }}
        >
          <div
            className="max-w-md w-full rounded-2xl p-8 text-center space-y-5"
            style={{ background: 'var(--color-surface-800)', border: '1px solid var(--color-border)' }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
              style={{ background: 'rgba(248,113,113,0.12)' }}
            >
              <AlertTriangle size={28} style={{ color: 'var(--color-miva-rose)' }} />
            </div>

            <div>
              <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Algo salió mal
              </h2>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Ocurrió un error inesperado en esta sección.
              </p>
              {this.state.error && (
                <p
                  className="text-xs mt-2 px-3 py-2 rounded-lg font-mono break-all"
                  style={{ background: 'var(--color-surface-700)', color: 'var(--color-text-muted)' }}
                >
                  {this.state.error.message}
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <button className="btn btn-ghost btn-sm" onClick={() => window.location.assign('/')}>
                <Home size={14} /> Inicio
              </button>
              <button className="btn btn-primary btn-sm" onClick={this.retry}>
                <RefreshCw size={14} /> Reintentar
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
